import React, { useRef, useState, useCallback, useEffect } from 'react';

interface SlideItem {
  src: string;
  alt: string;
}

interface GalleryZoomImageProps {
  index: number;
  items: SlideItem[];
  onIndexChange: (index: number) => void;
}

/**
 * 画廊灯箱 — 真三宫格跟手拖拽 + 缩放
 *
 * 核心原理：
 * - 轨道（track）内放3张图：[prev] [current] [next]，各占100vw
 * - 轨道初始 translateX(-100vw)，让 current 居中
 * - 拖动时轨道跟手移动，松手 snap 到相邻页或回弹
 * - 放大状态时拖动改为平移图片，不翻页
 */
const GalleryZoomImage: React.FC<GalleryZoomImageProps> = ({
  index,
  items,
  onIndexChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // 放大后的图片平移
  const [trackOffset, setTrackOffset] = useState(0); // 轨道偏移（相对初始-100vw的额外偏移）

  // 触摸状态 ref（避免闭包陷阱）
  const isDraggingRef = useRef(false);
  const gestureRef = useRef<'pan' | 'swipe' | null>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);
  const pinchStartRef = useRef<{ dist: number; scale: number } | null>(null);
  const dragStartOffsetRef = useRef(0);

  const total = items.length;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  // --- 翻页 ---
  const goTo = useCallback((newIndex: number) => {
    if (newIndex < 0 || newIndex >= total) return;
    setScale(1);
    setPan({ x: 0, y: 0 });
    setTrackOffset(0);
    onIndexChange(newIndex);
  }, [total, onIndexChange]);

  // 约束放大平移范围
  const clampPan = useCallback((x: number, y: number, s: number) => {
    if (s <= 1) return { x: 0, y: 0 };
    const el = containerRef.current;
    if (!el) return { x, y };
    const maxX = (el.offsetWidth * (s - 1)) / 2;
    const maxY = (el.offsetHeight * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, []);

  // 容器宽度
  const getW = useCallback(() => containerRef.current?.offsetWidth ?? window.innerWidth, []);

  // --- 触摸开始 ---
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // 双指 → 准备缩放
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartRef.current = { dist: Math.hypot(dx, dy), scale };
      isDraggingRef.current = true;
      gestureRef.current = null;
      return;
    }
    if (e.touches.length !== 1) return;

    const tx = e.touches[0].clientX;
    const ty = e.touches[0].clientY;
    touchStartRef.current = { x: tx, y: ty, time: Date.now() };
    lastTouchRef.current = { x: tx, y: ty };
    dragStartOffsetRef.current = trackOffset;
    isDraggingRef.current = true;
    gestureRef.current = null;
  }, [scale, trackOffset]);

  // --- 触摸移动 ---
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // 双指缩放
    if (e.touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const newScale = Math.min(5, Math.max(1, pinchStartRef.current.scale * (dist / pinchStartRef.current.dist)));
      const clamped = clampPan(pan.x * (newScale / scale), pan.y * (newScale / scale), newScale);
      setScale(newScale);
      setPan(clamped);
      return;
    }

    if (e.touches.length !== 1 || !isDraggingRef.current || !touchStartRef.current) return;

    const tx = e.touches[0].clientX;
    const ty = e.touches[0].clientY;

    // 首次移动：方向判断
    if (gestureRef.current === null) {
      const totalDx = tx - touchStartRef.current.x;
      const totalDy = ty - touchStartRef.current.y;
      if (Math.abs(totalDx) < 8 && Math.abs(totalDy) < 8) return;

      if (scale > 1) {
        gestureRef.current = 'pan';
      } else {
        gestureRef.current = Math.abs(totalDx) > Math.abs(totalDy) ? 'swipe' : 'pan';
        if (gestureRef.current === 'pan') {
          isDraggingRef.current = false;
          return;
        }
      }
    }

    e.preventDefault();

    if (gestureRef.current === 'swipe') {
      // 跟手偏移轨道
      let newOffset = dragStartOffsetRef.current + (tx - touchStartRef.current.x);

      // 橡皮筋
      if (!canPrev && newOffset > 0) newOffset *= 0.25;
      else if (!canNext && newOffset < 0) newOffset *= 0.25;

      setTrackOffset(newOffset);
    } else if (gestureRef.current === 'pan' && scale > 1) {
      const dx = tx - (lastTouchRef.current?.x ?? tx);
      const dy = ty - (lastTouchRef.current?.y ?? ty);
      const clamped = clampPan(pan.x + dx, pan.y + dy, scale);
      setPan(clamped);
    }

    lastTouchRef.current = { x: tx, y: ty };
  }, [scale, pan, canPrev, canNext, clampPan, getW]);

  // --- 触摸结束 ---
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length > 0) {
      pinchStartRef.current = null;
      return;
    }
    pinchStartRef.current = null;

    if (gestureRef.current === 'swipe' && touchStartRef.current) {
      const touch = e.changedTouches[0];
      if (touch) {
        const dx = touch.clientX - touchStartRef.current.x;
        const dt = Date.now() - touchStartRef.current.time;
        const w = getW();
        const velocity = Math.abs(dx) / dt;

        if (dx < 0 && (Math.abs(dx) > w * 0.2 || velocity > 0.3) && canNext) {
          goTo(index + 1);
        } else if (dx > 0 && (Math.abs(dx) > w * 0.2 || velocity > 0.3) && canPrev) {
          goTo(index - 1);
        } else {
          setTrackOffset(0);
        }
      } else {
        setTrackOffset(0);
      }
    }

    isDraggingRef.current = false;
    gestureRef.current = null;
    touchStartRef.current = null;
    lastTouchRef.current = null;
  }, [canPrev, canNext, goTo, index, getW]);

  // --- 鼠标（桌面端） ---
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      isDraggingRef.current = true;
      gestureRef.current = 'pan';
      lastTouchRef.current = { x: e.clientX, y: e.clientY };
    }
  }, [scale]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || gestureRef.current !== 'pan' || scale <= 1) return;
    const dx = e.clientX - (lastTouchRef.current?.x ?? e.clientX);
    const dy = e.clientY - (lastTouchRef.current?.y ?? e.clientY);
    lastTouchRef.current = { x: e.clientX, y: e.clientY };
    setPan(clampPan(pan.x + dx, pan.y + dy, scale));
  }, [scale, pan, clampPan]);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    gestureRef.current = null;
    lastTouchRef.current = null;
  }, []);

  // 滚轮缩放
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(5, Math.max(1, scale * delta));
    setPan(clampPan(pan.x * (newScale / scale), pan.y * (newScale / scale), newScale));
    setScale(newScale);
  }, [scale, pan, clampPan]);

  // 双击
  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      setScale(1);
      setPan({ x: 0, y: 0 });
    } else {
      setScale(3);
      setPan({ x: 0, y: 0 });
    }
  }, [scale]);

  // 键盘
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && canPrev) goTo(index - 1);
      if (e.key === 'ArrowRight' && canNext) goTo(index + 1);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [index, canPrev, canNext, goTo]);

  // 阻止背景滚动
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // 当前图片缩放平移样式
  const imgTransform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;

  // 轨道样式：初始偏移 -100vw + 用户拖拽偏移
  const isDragging = isDraggingRef.current;
  const trackStyle: React.CSSProperties = {
    transform: `translateX(calc(-100% + ${trackOffset}px))`,
    transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  };

  return (
    <div
      ref={containerRef}
      className="gallery-zoom-viewport"
      style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={() => { if (!isDragging) handleDoubleClick(); }}
    >
      <div className="gallery-slide-track" style={trackStyle}>
        {/* 前一张 */}
        <div className="gallery-slide-cell">
          {canPrev ? (
            <img
              src={items[index - 1].src}
              alt={items[index - 1].alt}
              className="gallery-zoom-image"
              draggable={false}
              style={{ transform: 'none', transition: 'none' }}
            />
          ) : <div />}
        </div>

        {/* 当前图 */}
        <div className="gallery-slide-cell">
          <img
            src={items[index].src}
            alt={items[index].alt}
            className="gallery-zoom-image"
            draggable={false}
            style={{
              transform: imgTransform,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            }}
          />
        </div>

        {/* 后一张 */}
        <div className="gallery-slide-cell">
          {canNext ? (
            <img
              src={items[index + 1].src}
              alt={items[index + 1].alt}
              className="gallery-zoom-image"
              draggable={false}
              style={{ transform: 'none', transition: 'none' }}
            />
          ) : <div />}
        </div>
      </div>
    </div>
  );
};

export default GalleryZoomImage;
